import { useEffect, useState } from 'react'
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline'
import { apiEngagementInsights } from './studioApi.js'
import { InfoCard, SkeletonText } from './ui-kit/index.js'

const ICON_SIZE = 11
const iconProps = { width: ICON_SIZE, height: ICON_SIZE, 'aria-hidden': true }

// EngagementInsightsSummary (Wave 27, refactored Wave 28) — surfaces
// "what's working / what's not" on the Dashboard so the operator
// doesn't have to navigate to AutonomyTab.EngagementBiasPanel for the
// daily glance. Three InfoCards in an auto-fit grid.

const MIN_SAMPLE = 3

function pickTopAndBottom(rollup) {
  if (!Array.isArray(rollup) || rollup.length === 0) {
    return { top: null, bottom: null }
  }
  const eligible = rollup.filter(
    (r) =>
      Number(r.sampleSize || 0) >= MIN_SAMPLE &&
      Number.isFinite(Number(r.biasFactor))
  )
  if (eligible.length === 0) return { top: null, bottom: null }
  const sorted = [...eligible].sort(
    (a, b) => Number(b.biasFactor) - Number(a.biasFactor)
  )
  const top = sorted[0]
  const bottom = sorted.length > 1 ? sorted[sorted.length - 1] : null
  return { top, bottom }
}

function pillarLabel(getPillar, pillarId) {
  if (typeof getPillar === 'function') {
    return getPillar(pillarId)?.label || pillarId
  }
  return pillarId
}

export function EngagementInsightsSummary({ getPillar, onOpenTab }) {
  const [insights, setInsights] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const data = await apiEngagementInsights()
        if (mounted) {
          setInsights(data)
          setError('')
        }
      } catch (err) {
        if (mounted) {
          setError(err?.message || 'Failed to load engagement insights')
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void load()
    return () => {
      mounted = false
    }
  }, [])

  const rollup = insights?.pillarRollup || []
  const voiceRollup = insights?.voiceBandRollup || []
  const { top, bottom } = pickTopAndBottom(rollup)

  const sortedVoiceRollup = [...voiceRollup]
    .filter((r) => Number(r.sampleSize || 0) >= MIN_SAMPLE)
    .sort((a, b) => {
      const order = ['high', 'medium', 'low', 'unknown']
      return order.indexOf(a.band) - order.indexOf(b.band)
    })

  const bottomBiasLow = bottom && Number(bottom.biasFactor) < 0.7

  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}
    >
      {/* Top performing pillar */}
      <InfoCard
        label="Top performing pillar"
        icon={<ArrowTrendingUpIcon {...iconProps} />}
        tone={top ? 'success' : 'neutral'}
        onClick={() => onOpenTab && onOpenTab('autonomy')}
      >
        {loading ? (
          <SkeletonText width="70%" height={16} marginBottom={4} />
        ) : error ? (
          <div style={{ fontSize: 12, opacity: 0.5 }}>fetch failed</div>
        ) : top ? (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#4a9b6f' }}>
              {pillarLabel(getPillar, top.pillar)}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
              {Number(top.biasFactor).toFixed(2)}× bias · {top.sampleSize} posts
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.5 }}>
            insufficient sample (need {MIN_SAMPLE}+ per pillar)
          </div>
        )}
      </InfoCard>

      {/* Underperforming pillar */}
      <InfoCard
        label="Underperforming pillar"
        icon={<ArrowTrendingDownIcon {...iconProps} />}
        tone={bottomBiasLow ? 'error' : 'neutral'}
        onClick={() => onOpenTab && onOpenTab('autonomy')}
      >
        {loading ? (
          <SkeletonText width="70%" height={16} marginBottom={4} />
        ) : error ? (
          <div style={{ fontSize: 12, opacity: 0.5 }}>fetch failed</div>
        ) : bottom ? (
          <>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: bottomBiasLow ? '#f06464' : '#d4582d',
              }}
            >
              {pillarLabel(getPillar, bottom.pillar)}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
              {Number(bottom.biasFactor).toFixed(2)}× bias · {bottom.sampleSize}{' '}
              posts
              {bottomBiasLow ? ' · candidate for deprecation review' : ''}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, opacity: 0.5 }}>
            no underperformers detected
          </div>
        )}
      </InfoCard>

      {/* Voice band correlation */}
      <InfoCard
        label="Voice band → engagement"
        icon={<MicrophoneIcon {...iconProps} />}
      >
        {loading ? (
          <SkeletonText width="70%" height={16} marginBottom={4} />
        ) : error ? (
          <div style={{ fontSize: 12, opacity: 0.5 }}>fetch failed</div>
        ) : sortedVoiceRollup.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.5 }}>insufficient sample</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sortedVoiceRollup.map((row) => {
              const rate = Number(row.avgEngagementRate || 0)
              const pct = Math.min(100, rate * 1000)
              return (
                <div key={row.band}>
                  <div
                    style={{
                      fontSize: 11,
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 2,
                    }}
                  >
                    <span style={{ textTransform: 'capitalize' }}>
                      {row.band}
                    </span>
                    <span style={{ opacity: 0.7 }}>
                      {(rate * 100).toFixed(2)}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 3,
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        background:
                          row.band === 'high'
                            ? '#4a9b6f'
                            : row.band === 'medium'
                              ? '#d4582d'
                              : row.band === 'low'
                                ? '#f06464'
                                : '#88827a',
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </InfoCard>
    </section>
  )
}

// Exported for tests.
export { pickTopAndBottom }
